import React from "react";
import { Box, Grid, Paper, Skeleton, Stack, Divider } from "@mui/material";

/**
 * Skeleton loader for the Settings page. Mirrors the high-level layout
 * (back link → page title → profile card → wallet/security card → preferences card)
 * so the loading state has the same shape as the loaded page.
 */
export default function SettingsSkeleton() {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        py: 4,
        px: 2,
        display: "flex",
      }}
    >
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {/* Back link */}
        <Skeleton variant="text" width={140} height={28} sx={{ mb: 2 }} />

        {/* Page title */}
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={220} height={36} />
          <Skeleton variant="text" width={260} height={20} />
        </Box>

        {/* PROFILE INFORMATION */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: 3,
            mb: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Skeleton variant="text" width={180} height={28} />
          <Skeleton variant="text" width={280} height={20} sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Left column: avatar + name + email/phone */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2.5}>
                <Skeleton variant="circular" width={64} height={64} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={18} />
                  <Skeleton
                    variant="rounded"
                    width={140}
                    height={22}
                    sx={{ mt: 1, borderRadius: 999 }}
                  />
                </Box>
              </Stack>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Skeleton variant="text" width={90} height={16} />
                  <Skeleton variant="text" width="80%" height={20} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Skeleton variant="text" width={100} height={16} />
                  <Skeleton variant="text" width="70%" height={20} />
                </Grid>
              </Grid>
            </Grid>

            {/* Right column: identity cards */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Skeleton
                variant="rounded"
                height={88}
                sx={{ borderRadius: 2, mb: 2 }}
              />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Skeleton
                    variant="rounded"
                    height={120}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Skeleton
                    variant="rounded"
                    height={120}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* KYC banner */}
          <Skeleton
            variant="rounded"
            height={56}
            sx={{ borderRadius: 2, mt: 3 }}
          />
        </Paper>

        {/* LINKED WALLET + SECURITY */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: 3,
            mb: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Skeleton variant="text" width={160} height={26} />
          <Skeleton variant="text" width={300} height={18} sx={{ mb: 2 }} />

          {/* Wallet card */}
          <Skeleton
            variant="rounded"
            height={180}
            sx={{ borderRadius: 3, mb: 3 }}
          />

          <Skeleton variant="text" width={120} height={26} />
          <Skeleton variant="text" width={260} height={18} sx={{ mb: 2 }} />

          <Stack spacing={2}>
            {[0, 1].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={64}
                sx={{ borderRadius: 2 }}
              />
            ))}
            <Skeleton
              variant="rounded"
              width={180}
              height={36}
              sx={{ borderRadius: 999, mt: 1 }}
            />
          </Stack>
        </Paper>

        {/* PREFERENCES + PRIVACY + INVESTMENT + NOTIFICATIONS */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: 3,
            mb: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Skeleton variant="text" width={140} height={26} />
          <Skeleton variant="text" width={320} height={18} sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Skeleton variant="text" width={160} height={22} sx={{ mb: 1 }} />
              <Stack spacing={1.5}>
                {[0, 1, 2, 3].map((i) => (
                  <Box key={i}>
                    <Skeleton variant="text" width={90} height={14} />
                    <Skeleton variant="text" width="60%" height={20} />
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Skeleton variant="text" width={120} height={22} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={70} height={14} sx={{ mb: 1.5 }} />
              <Grid container spacing={1.5}>
                {[0, 1, 2].map((i) => (
                  <Grid size={{ xs: 12, md: 4 }} key={i}>
                    <Skeleton
                      variant="rounded"
                      height={48}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Privacy toggles */}
          <Skeleton variant="text" width={100} height={26} sx={{ mb: 1.5 }} />
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={64}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>

          {/* Investment preferences */}
          <Skeleton variant="text" width={200} height={26} sx={{ mb: 1.5 }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[0, 1, 2].map((i) => (
              <Grid size={{ xs: 12, md: 4 }} key={i}>
                <Skeleton
                  variant="rounded"
                  height={72}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Notifications */}
          <Skeleton variant="text" width={140} height={26} />
          <Skeleton variant="text" width={280} height={18} sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[0, 1, 2].map((i) => (
              <Grid size={{ xs: 12, md: 4 }} key={i}>
                <Skeleton
                  variant="rounded"
                  height={56}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
            ))}
          </Grid>
          <Stack spacing={1.5}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={64}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
